import { RemoteError, type AccessLevel, type Op, type Remote, type RemoteOpMeta } from './types';

/**
 * In-memory stand-in for the business folder, for tests. It models the failure modes we must survive:
 * server-assigned createdTime, listing that lags behind writes, writes whose response is lost,
 * offline, revoked access and quota errors.
 */
export class MemoryServer {
  files: { meta: RemoteOpMeta; body: string; visibleAt: number }[] = [];
  clock = Date.parse('2026-10-03T05:00:00Z');
  listingLagMs = 0;
  access = new Map<string, AccessLevel>();
  quotaFull = false;
  private n = 0;
  tick(ms = 1000) { this.clock += ms; }
  client(account: string, access: AccessLevel = 'editor') { this.access.set(account, access); return new MemoryRemote(this, account); }
  create(op: Op): RemoteOpMeta {
    const existing = this.files.find(f => f.meta.opId === op.opId);
    if (existing) return existing.meta;
    this.tick(1);
    const meta = { opId: op.opId, fileId: `f${++this.n}`, createdTime: new Date(this.clock).toISOString() };
    this.files.push({ meta, body: JSON.stringify(op), visibleAt: this.clock + this.listingLagMs });
    return meta;
  }
}

export class MemoryRemote implements Remote {
  offline = false;
  /** Next createOp writes the file but the response is lost (timeout after the server accepted it). */
  loseNextResponse = false;
  /** Next createOp fails before anything is written. */
  failNextWrite = false;
  constructor(private server: MemoryServer, private account: string) {}
  private guard(write = false) {
    if (this.offline) throw new RemoteError('offline', 'No connection');
    const a = this.server.access.get(this.account) ?? 'none';
    if (a === 'none') throw new RemoteError('denied', 'This account no longer has access to the folder');
    if (write && a !== 'editor') throw new RemoteError('denied', 'View-only access');
  }
  async access() { this.guard(); return this.server.access.get(this.account) ?? 'none'; }
  async listOps() { this.guard(); return this.server.files.filter(f => f.visibleAt <= this.server.clock).map(f => ({ ...f.meta })); }
  async readOp(meta: RemoteOpMeta) {
    this.guard();
    const f = this.server.files.find(x => x.meta.fileId === meta.fileId);
    if (!f) throw new RemoteError('not-found', 'Missing file');
    return JSON.parse(f.body) as Op;
  }
  async createOp(op: Op) {
    this.guard(true);
    if (this.server.quotaFull) throw new RemoteError('quota', 'OneDrive storage is full');
    if (this.failNextWrite) { this.failNextWrite = false; throw new RemoteError('offline', 'Connection dropped'); }
    const meta = this.server.create(op);
    if (this.loseNextResponse) { this.loseNextResponse = false; throw new RemoteError('offline', 'Connection dropped after sending'); }
    return meta;
  }
}
